@if (isLoading()) {
  <lib-overlay-loader />
} @else if (details(); as delegation) {
  <div class="flex flex-col gap-2xl">
    <header
      class="flex items-start justify-between gap-md pb-lg border-b border-neutral-200">
      <div class="flex items-start gap-md">
        <button
          type="button"
          id="delegation_details_back_button"
          (click)="goBack()">
          <lib-chevron-down-icon
            [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
        </button>

        <div class="flex flex-col gap-x-sm">
          <div class="flex items-center gap-md">
            <h1 class="text-xl font-semibold text-neutral-900">
              {{ 'delegation_details_screen_title' | transloco }}
            </h1>

            @if (delegation.state) {
              <lib-badge
                [label]="delegation.state.label"
                [badgeClass]="
                  'px-sm border-none text-xs font-medium text-white ' +
                  statusBadgeClass()
                " />
            }
          </div>

          <p class="text-sm font-medium text-neutral-500">
            # {{ delegation.id }}
          </p>
        </div>
      </div>

      @if (actionMenuItems().length) {
        <lib-action-list
          [menuItems]="actionMenuItems()"
          (itemClick)="onActionClick($event)" />
      }
    </header>

    <div class="flex items-center justify-between gap-md">
      <h2 class="text-lg font-semibold text-neutral-900">
        {{ 'delegation_details_screen_title' | transloco }}
      </h2>
      <lib-expand-collapse-all
        [(isAllSectionsExpanded)]="isAllSectionsExpanded" />
    </div>

    <lib-section-header
      [title]="'delegation_form_screen_details_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="detailsSection">
      <ng-template #detailsSection>
        <div
          class="grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg mt-xl">
          <div>
            <p class="view-mode-label">
              {{ 'delegation_form_screen_delegate_to_label' | transloco }}
            </p>
            <div class="view-mode-value flex items-center gap-sm">
              <lib-avatar
                [size]="'list'"
                [img]="delegation.delegate.image?.url ?? ''"
                [fullName]="delegation.delegate.name"
                borderRadius="rounded-full" />
              <span class="text-sm font-medium text-neutral-900">
                {{
                  delegation.delegate.name ||
                    ('details_not_found_label' | transloco)
                }}
              </span>
            </div>
          </div>

          <div>
            <p class="view-mode-label">
              {{ 'delegation_form_screen_period_from_label' | transloco }}
            </p>
            <p class="view-mode-value break-word text-neutral-900" dir="ltr">
              {{
                delegation.started_at
                  ? (delegation.started_at | date: 'dd-MMM-yyyy')
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{ 'delegation_form_screen_period_to_label' | transloco }}
            </p>
            <p class="view-mode-value break-word text-neutral-900" dir="ltr">
              {{
                delegation.ended_at
                  ? (delegation.ended_at | date: 'dd-MMM-yyyy')
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div class="col-[1/3]">
            <p class="view-mode-label">
              {{ 'delegation_form_screen_reason_label' | transloco }}
            </p>
            @if (reasonText()) {
              <div
                class="view-mode-value break-word text-neutral-900"
                [innerHTML]="reasonText() | sanitizeHtml"></div>
            } @else {
              <p class="view-mode-value break-word text-neutral-900">
                {{ 'details_not_found_label' | transloco }}
              </p>
            }
          </div>
        </div>
      </ng-template>
    </lib-section-header>

    <lib-section-header
      [title]="'delegation_form_screen_workflows_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="workflowsSection">
      <ng-template #workflowsSection>
        @if (delegation.flow_templates.length) {
          <div
            class="grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg mt-xl">
            @for (workflow of delegation.flow_templates; track workflow.id) {
              <div
                class="relative flex items-center h-16 border border-neutral-200 rounded-lg px-3 gap-2">
                <lib-avatar
                  [imgDimensions]="{ width: 32, height: 32 }"
                  [img]="workflow.icon?.url ?? ''"
                  [fullName]="workflow.name"
                  [size]="''"
                  [borderRadius]="'rounded-full'"
                  class="shrink-0 self-center" />
                <span
                  class="flex-1 text-sm font-semibold text-neutral-900 truncate">
                  {{ workflow.name }}
                </span>
                <button
                  type="button"
                  libTippyTooltip
                  #workflowTooltip="libTippyTooltip"
                  [contentTemplate]="workflowDetailsTooltip"
                  placement="top"
                  trigger="click"
                  interactive
                  [toolTipClass]="'bg-white rounded-lg shadow-lg min-w-[31rem] p-2xl'"
                  class="bg-transparent border-none p-0 cursor-pointer shrink-0 self-center leading-none">
                  <lib-info-icon
                    iconClass="size-5 text-primary-500 shrink-0 self-center" />
                </button>
                <ng-template #workflowDetailsTooltip>
                  <div class="flex items-center gap-sm mb-lg">
                    <lib-avatar
                      [imgDimensions]="{ width: 40, height: 40 }"
                      [img]="workflow.icon?.url ?? ''"
                      [fullName]="workflow.name"
                      [size]="''"
                      [borderRadius]="'rounded-full'"
                      class="shrink-0" />
                    <p
                      class="flex-1 min-w-0 text-sm font-semibold text-neutral-900 truncate">
                      {{ workflow.name }}
                    </p>
                    <button
                      type="button"
                      class="bg-transparent border-none p-0 cursor-pointer shrink-0 leading-none"
                      (click)="workflowTooltip.hide()">
                      <lib-close-icon
                        iconClass="size-5"
                        stroke="var(--color-neutral-500)" />
                    </button>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <p class="text-xs font-medium text-neutral-500">
                        {{
                          'delegation_form_screen_description_label' | transloco
                        }}
                      </p>
                      <p class="text-sm font-semibold text-neutral-900 mt-x-sm">
                        {{
                          workflow.description ||
                            ('details_not_found_label' | transloco)
                        }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs font-medium text-neutral-500">
                        {{ 'delegation_form_screen_category_label' | transloco }}
                      </p>
                      <p class="text-sm font-semibold text-neutral-900 mt-x-sm">
                        {{
                          workflow.category.name ||
                            ('details_not_found_label' | transloco)
                        }}
                      </p>
                    </div>
                  </div>
                </ng-template>
              </div>
            }
          </div>
        } @else {
          <p class="view-mode-value break-word text-neutral-900 mt-xl">
            {{ 'details_not_found_label' | transloco }}
          </p>
        }
      </ng-template>
    </lib-section-header>

    <lib-section-header
      [title]="'delegation_form_screen_tasks_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="tasksSection">
      <ng-template #tasksSection>
        <div class="mt-xl">
          <p class="view-mode-label">
            {{ 'delegation_form_screen_tasks_section_title' | transloco }}
          </p>
          <p class="view-mode-value break-word text-neutral-900">
            {{
              taskScopeLabel()
                ? (taskScopeLabel() | transloco)
                : ('details_not_found_label' | transloco)
            }}
          </p>
        </div>
      </ng-template>
    </lib-section-header>
  </div>
}
